import React, { useEffect, useState } from "react";
import { Save, X } from "lucide-react";

const FEATURES = [
  "ALL_FEATURES",
  "ADVANCED_ANALYTICS",
  "ENTERPRISE_SUPPORT"
];

export default function LicenseForm({
  onSubmit,
  isLoading,
  clients = [],
  partners = [],
  rans = []
}) {
  const [form, setForm] = useState({
    clientId: "",
    partnerId: "",
    licenseFor: "",
    ranId: "",
    hardwareFingerprint: "", // For Compass licenses
    licenseType: "TRIAL",
    startDate: "",
    endDate: "",
    features: []
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (form.licenseType === "PERPETUAL") {
      setForm(prev => ({ ...prev, endDate: "" }));
    }
  }, [form.licenseType]);

  useEffect(() => {
    // Clear ranId and hardwareFingerprint when licenseFor changes
    if (form.licenseFor !== "ran") {
      setForm(prev => ({ ...prev, ranId: "" }));
    }
    if (form.licenseFor !== "compass") {
      setForm(prev => ({ ...prev, hardwareFingerprint: "" }));
    }
  }, [form.licenseFor]);

  /* VALIDATION */
  const validate = () => {
    const e = {};

    if (!form.clientId) e.clientId = "Client is required";
    if (!form.partnerId) e.partnerId = "Partner is required";
    if (!form.licenseFor) e.licenseFor = "License For is required";
    if (form.licenseFor === "ran" && !form.ranId) e.ranId = "RAN is required";
    if (form.licenseFor === "compass" && !form.hardwareFingerprint) 
      e.hardwareFingerprint = "Hardware fingerprint is required for Compass licenses";
    if (!form.startDate) {
      e.startDate = "Start date required";
    } else {
      // Prevent past dates
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (new Date(form.startDate) < today) {
        e.startDate = "Valid From cannot be in the past";
      }
    }

    if (form.licenseType === "TRIAL") {
      if (!form.endDate) {
        e.endDate = "End date required";
      } else if (new Date(form.endDate) <= new Date(form.startDate)) {
        e.endDate = "End date must be after start date";
      }
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };


  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      clientId: form.clientId,
      partnerId: form.partnerId,
      licenseFor: form.licenseFor,
      licenseType: form.licenseType,
      startDate: form.startDate,
      features: form.features
    };

    // ONLY include ranId for RAN licenses
    if (form.licenseFor === "ran") {
      payload.ranId = form.ranId;
    }

    // ONLY include hardwareFingerprint for Compass licenses
    if (form.licenseFor === "compass") {
      payload.hardwareFingerprint = form.hardwareFingerprint;
    }

    // ONLY include endDate for TRIAL
    if (form.licenseType === "TRIAL") {
      payload.endDate = form.endDate;
    }

    onSubmit(payload);
  };


  const addFeature = (feature) => {
    if (!feature || form.features.includes(feature)) return;
    setForm(prev => ({
      ...prev,
      features: [...prev.features, feature]
    }));
  };

  const removeFeature = (feature) => {
    setForm(prev => ({
      ...prev,
      features: prev.features.filter(f => f !== feature)
    }));
  };

  const availableFeatures = FEATURES.filter(
    f => !form.features.includes(f)
  );

  const inputBase = "w-full h-10 border rounded-lg px-3 transition";
  const textareaBase = "w-full px-3 py-2 border rounded-lg transition resize-none";
  const fieldLabelStyle = { color: "var(--main-text-muted)" };
  const inputStyle = {
    background: "var(--input-background)",
    color: "var(--main-text)",
    borderColor: "var(--card-border)"
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">

      {/* CLIENT */}
      <Select
        label="Client"
        value={form.clientId}
        options={clients}
        labelKey="clientName"
        onChange={(v) => setForm({ ...form, clientId: v })}
        error={errors.clientId}
      />

      {/* PARTNER */}
      <Select
        label="Partner"
        value={form.partnerId}
        options={partners}
        labelKey="partnerName"
        onChange={(v) => setForm({ ...form, partnerId: v })}
        error={errors.partnerId}
      />

      {/* LICENSE FOR */}
      <div>
        <label className="text-sm mb-1 block" style={fieldLabelStyle}>
          License For
        </label>
        <select
          value={form.licenseFor}
          onChange={(e) => setForm({ ...form, licenseFor: e.target.value })}
          className={inputBase}
          style={inputStyle}
        >
          <option value="">Select License For</option>
          <option value="ran">RAN</option>
          <option value="compass">Compass</option>
        </select>
        {errors.licenseFor && (
          <p className="text-xs mt-1" style={{ color: "var(--danger)" }}>{errors.licenseFor}</p>
        )}
      </div>

      {/* RAN - Only show when License For is RAN */}
      {form.licenseFor === "ran" && (
        <Select
          label="RAN"
          value={form.ranId}
          options={rans}
          labelKey="ranID"
          onChange={(v) => setForm({ ...form, ranId: v })}
          error={errors.ranId}
        />
      )}

      {/* Hardware Fingerprint - Only show when License For is Compass */}
      {form.licenseFor === "compass" && (
        <div>
          <label className="text-sm mb-1 block" style={fieldLabelStyle}>
            Hardware Fingerprint
          </label>
          <input
            type="text"
            value={form.hardwareFingerprint}
            onChange={(e) => setForm({ ...form, hardwareFingerprint: e.target.value })}
            placeholder="Enter hardware fingerprint manually"
            className={inputBase}
            style={inputStyle}
          />
          {errors.hardwareFingerprint && (
            <p className="text-xs mt-1" style={{ color: "var(--danger)" }}>{errors.hardwareFingerprint}</p>
          )}
        </div>
      )}

      {/* LICENSE TYPE */}
      <div>
        <label className="text-sm mb-1 block" style={fieldLabelStyle}>
          License Type
        </label>
        <select
          value={form.licenseType}
          onChange={(e) =>
            setForm({ ...form, licenseType: e.target.value })
          }
          className={inputBase}
          style={inputStyle}
        >
          <option value="TRIAL">Trial</option>
          <option value="PERPETUAL">Perpetual</option>
        </select>
      </div>

      {/* DATES */}
      <div className="grid grid-cols-2 gap-4">
        <DateField
          label="Valid From"
          value={form.startDate}
          onChange={(v) => setForm({ ...form, startDate: v })}
          error={errors.startDate}
        />

        {form.licenseType === "TRIAL" && (
          <DateField
            label="Valid Until"
            value={form.endDate}
            onChange={(v) => setForm({ ...form, endDate: v })}
            error={errors.endDate}
          />
        )}
      </div>

      {/* FEATURES (CHIPS) */}
      <div>
        <label className="block text-sm font-medium mb-2" style={{ color: 'var(--main-text-muted)' }}>
          Features
        </label>

        {/* Selected Chips */}
        <div className="min-h-[44px] w-full rounded-lg px-3 py-2 flex flex-wrap gap-2" style={{ background: 'var(--input-background)', border: '1px solid var(--card-border)' }}>
          {form.features.length === 0 && (
            <span className="text-sm" style={{ color: 'var(--main-text-muted)' }}>
              No features selected. Choose from dropdown below.
            </span>
          )}

          {form.features.map(feature => (
            <span
              key={feature}
              className="flex items-center gap-2 px-3 py-1 rounded-full text-sm"
              style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', color: 'var(--main-text)' }}
            >
              {feature}
              <button
                type="button"
                onClick={() => removeFeature(feature)}
                className="hover:text-red-400"
                style={{ color: 'var(--main-text-muted)' }}
              >
                <X size={14} />
              </button>
            </span>
          ))}
        </div>

        {/* Dropdown */}
        <select
          onChange={(e) => {
            addFeature(e.target.value);
            e.target.value = "";
          }}
          className={inputBase}
          style={inputStyle}
        >
          <option value="" disabled>
            Select feature to add
          </option>
          {availableFeatures.map(f => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
        </select>

        {errors.features && (
          <p className="mt-1 text-xs text-red-400">
            {errors.features}
          </p>
        )}
      </div>

      {/* SUBMIT */}
      <button
        disabled={isLoading}
        className="w-full h-10 rounded-lg text-white font-medium flex items-center justify-center gap-2"
        style={{
          background: isLoading ? 'var(--card-border)' : 'var(--brand)',
          cursor: isLoading ? 'not-allowed' : 'pointer'
        }}
      >
        <Save size={16} />
        {isLoading ? "Generating License..." : "Generate License"}
      </button>
    </form>
  );
}

const Select = ({ label, value, onChange, options, labelKey, error }) => (
  <div>
    <label className="text-sm mb-1 block" style={{ color: 'var(--main-text-muted)' }}>
      {label}
    </label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full h-10 border rounded-lg px-3 transition"
      style={{
        background: 'var(--input-background)',
        color: 'var(--main-text)',
        borderColor: 'var(--card-border)'
      }}
    >
      <option value="">Select {label}</option>
      {options.map(o => (
        <option key={o._id} value={o._id}>
          {o[labelKey]}
        </option>
      ))}
    </select>
    {error && (
      <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>{error}</p>
    )}
  </div>
);

const DateField = ({ label, value, onChange, error }) => (
  <div>
    <label className="text-sm mb-1 block" style={{ color: 'var(--main-text-muted)' }}>
      {label}
    </label>
    <input
      type="date"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full h-10 border rounded-lg px-3 transition"
      style={{
        background: 'var(--input-background)',
        color: 'var(--main-text)',
        borderColor: 'var(--card-border)'
      }}
    />
    {error && (
      <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>{error}</p>
    )}
  </div>
);
